/**
 * CharacterPoseConfig.js
 *
 * Source of truth for per-character pose data: intrinsic eye height,
 * face trim nudge, and a global anchor offset. Persisted in localStorage.
 *
 * The compute pipeline (see SceneFraming.js) is:
 *
 *     effectiveAnchorY = SCENE_FRAMING.faceAnchorY + globalAnchorOffset
 *     model.position.y    = effectiveAnchorY - eyeHeight + faceTrim
 *     platform.position.y = model.position.y - SCENE_FRAMING.platformVerticalOffset
 *
 * Eye-height resolution priority:
 *   1) manual lock (user clicked "Override with current")
 *   2) eyeHeightOverride (data-side schema override)
 *   3) runtime-detected this load (skeleton -> Box3 fallback)
 *   4) cached detected value from a previous load
 *   5) per-character configured default in CHARACTER_POSES
 *   6) generic DEFAULT_EYE_HEIGHT
 */

import * as THREE from "three";
import { SCENE_FRAMING } from "./SceneFraming.js";

export const STORAGE_KEY = "akadimia.characterPoses.v1";
export const GLOBAL_ANCHOR_KEY = "akadimia.globalFaceAnchorOffset.v1";

export const DEFAULT_EYE_HEIGHT = 165;
export const ANTHROPOMETRIC_EYE_RATIO = 0.93;

/**
 * Per-character pose registry.
 * Keys are character UUIDs (must match modelViewer.currentModelKey).
 *
 * Schema per entry:
 *   { name, eyeHeight, faceTrim }
 *
 * Seeded from the legacy ModelPlatformConfig entries with neutral defaults;
 * the skeleton detector self-corrects on first load.
 */
export const CHARACTER_POSES = {
    "73f610bf-d9c9-4d9f-a026-a10086a66725": {
        name: "Marie Curie",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "d537d61d-0313-421b-ae66-48a8d7222b3f": {
        name: "Nikola Tesla",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "7c7ac397-c36e-4218-92d4-f437d1959cca": {
        name: "Euclid",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "f9f3f836-00c6-4160-8416-4b71b53f1307": {
        name: "Srinivasa Ramanujan",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "5802e813-e715-491d-a0fd-92dc643c458e": {
        name: "Blaise Pascal",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "191e6103-a42c-4132-926c-230e7b1be5b6": {
        name: "Jacob Bernoulli",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "6bcf3678-e232-46c9-996a-67beb3fd2dbf": {
        name: "Al-Khwarizmi",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "14836d13-ace6-4bf8-be26-26fbb80ed9bc": {
        name: "Socrates",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "3841a6bb-f977-453d-92f5-6cec1267546f": {
        name: "Sigmund Freud",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "c0063a05-916b-45d0-bc2c-c63210414b17": {
        name: "Ada Lovelace",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "7f61d092-99e4-48e6-bf80-7aa2b7bd98ff": {
        name: "Carl Friedrich Gauss",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "127cd993-f16e-4659-84fa-882cba633097": {
        name: "Galileo Galilei",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "94b6efbe-0aaa-47c7-bd3f-6d4f441d767f": {
        name: "Grace Hopper",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "c93478a1-ed2f-4f1a-81f6-3fa474a21566": {
        name: "Joseph-Louis Lagrange",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "5a39bed2-a551-4a03-98a2-46104a3324bf": {
        name: "Leonhard Euler",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "2707ca40-df64-4258-97c8-67d3695e8a42": {
        name: "Brahmagupta",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "e617290b-bda0-4771-91ac-3fe6939dd412": {
        name: "Benjamin Franklin",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "a2a43496-9a83-4444-813e-18a1930296e6": {
        name: "Omar Khayyam",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "78376372-3d71-4fce-8650-82b5be2c006b": {
        name: "Diophantus",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "ca6ebdca-5151-4a58-8523-56d25fce5dfa": {
        name: "William Playfair",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "e3428624-bd1e-48b9-ae8c-c66ba68879cc": {
        name: "George Boole",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "3140d634-c7ca-4bda-af36-e1b49978b201": {
        name: "Thomas Bayes",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "c73fed54-81e3-46ec-9d7c-3d11d05b142d": {
        name: "Eratosthenes",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "ece0ab37-37c1-4ead-8222-fbb5b185dbe2": {
        name: "Alessandro Volta",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "d13b5a81-ed77-4a25-8a01-a76a0165b349": {
        name: "Archimedes",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "c4b9ac58-a66a-451e-b035-afabd8be50b9": {
        name: "Aristotle",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "e97568c5-1daa-4a95-b4ae-0fe374c6b083": {
        name: "Marcus Aurelius",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "2db1d398-0161-46cd-8aff-53008d29a9dc": {
        name: "Claude Shannon",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "8f67d579-41c2-456d-900b-fccbcbf46bd3": {
        name: "François Viète",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "ec6c5b20-8ac6-42bd-b291-f248cb3f0ebc": {
        name: "Felix Klein",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "44f87c0b-ca22-4225-9fea-8d823e6a0160": {
        name: "Warren Buffett",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },

    "770e2d93-a6a4-41ed-aca1-8951327885b5": {
        name: "Amelia Earhart",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "8944783c-2e2b-4796-b92e-21dacca776bc": {
        name: "Leonardo da Vinci",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: -5,
    },
    "3542f19e-4e07-4828-8f9d-f330e4d6d660": {
        name: "Plato",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "b8951237-698b-42b5-8b6e-ee381b91fb65": {
        name: "Confucius",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "4f50a698-6034-49c1-b9d4-221f59bf2378": {
        name: "Frida Kahlo",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "91d5cec5-1c21-423f-bbb4-bceb0f77a6c9": {
        name: "Nostradamus",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "581e7293-43cf-45ea-9fb3-0955c563cbe2": {
        name: "Genghis Khan",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "d6e3a3f1-d31e-4aab-8dd0-8b820932bd13": {
        name: "Enrico Fermi",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "2baa0479-7e38-4473-a2f2-1ad54149dbac": {
        name: "Muhammad Ali",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "be0dcf0f-c72b-48f7-a462-6ba7560498d7": {
        name: "Alan Turing",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "16f5b6a1-45f6-4c01-a612-60d4e4ab2297": {
        name: "René Descartes",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "88c314c4-5890-4e55-9418-8b46a376ef81a": {
        name: "Friedrich Nietzsche",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "278dddd6-b4a8-4303-a41d-be6219dbc907": {
        name: "Albert Einstein",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "031f2eea-64e5-441d-a37d-03d260cc43dc": {
        name: "Abraham Lincoln",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "b20dc26f-8924-4aee-9ef5-9b98c0367a34": {
        name: "Ernest Hemingway",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "5a42d57c-6e2b-4ba8-bd32-838a1a5c5e6b": {
        name: "Niccolo Machiavelli",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
    "e3f55c33-3ff0-471f-b833-b59d7b145fc6": {
        name: "Al Capone",
        eyeHeight: DEFAULT_EYE_HEIGHT,
        faceTrim: 0,
    },
};

function safeStorage() {
    try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        return window.localStorage;
    } catch (_e) {
        return null;
    }
}

export function loadOverrides() {
    const ls = safeStorage();
    if (!ls) return {};
    try {
        const raw = ls.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
        console.warn("[CharacterPoseConfig] Failed to parse overrides:", e);
        return {};
    }
}

function persistOverrides(map) {
    const ls = safeStorage();
    if (!ls) return;
    try {
        ls.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (e) {
        console.warn("[CharacterPoseConfig] Failed to persist overrides:", e);
    }
}

export function saveOverride(modelId, partial) {
    if (!modelId || !partial || typeof partial !== "object") return;
    const map = loadOverrides();
    const existing = map[modelId] || {};
    map[modelId] = { ...existing, ...partial };
    persistOverrides(map);
}

export function clearOverride(modelId) {
    if (!modelId) return;
    const map = loadOverrides();
    if (modelId in map) {
        delete map[modelId];
        persistOverrides(map);
    }
}

export function getGlobalFaceAnchorOffset() {
    const ls = safeStorage();
    if (!ls) return 0;
    try {
        const raw = ls.getItem(GLOBAL_ANCHOR_KEY);
        if (raw == null) return 0;
        const v = parseFloat(raw);
        return Number.isFinite(v) ? v : 0;
    } catch (_e) {
        return 0;
    }
}

export function setGlobalFaceAnchorOffset(value) {
    const ls = safeStorage();
    if (!ls) return;
    const v = Number.isFinite(value) ? value : 0;
    try {
        ls.setItem(GLOBAL_ANCHOR_KEY, String(v));
    } catch (e) {
        console.warn(
            "[CharacterPoseConfig] Failed to persist global anchor offset:",
            e,
        );
    }
}

export function clearGlobalFaceAnchorOffset() {
    const ls = safeStorage();
    if (!ls) return;
    try {
        ls.removeItem(GLOBAL_ANCHOR_KEY);
    } catch (_e) {
        /* noop */
    }
}

/**
 * Merge base CHARACTER_POSES entry with any localStorage override.
 * Returns a defensive shallow copy.
 */
export function getPoseConfig(modelId) {
    const base = (modelId && CHARACTER_POSES[modelId]) || null;
    const override = (modelId && loadOverrides()[modelId]) || null;
    const merged = {
        name: (base && base.name) || "Unknown",
        eyeHeight:
            base && Number.isFinite(base.eyeHeight)
                ? base.eyeHeight
                : DEFAULT_EYE_HEIGHT,
        faceTrim: base && Number.isFinite(base.faceTrim) ? base.faceTrim : 0,
        eyeHeightOverride: undefined,
        detectedEyeHeight: undefined,
        manualLock: false,
        scale: undefined,
    };
    if (override) {
        if (Number.isFinite(override.eyeHeight)) {
            merged.eyeHeight = override.eyeHeight;
        }
        if (Number.isFinite(override.faceTrim)) {
            merged.faceTrim = override.faceTrim;
        }
        if (Number.isFinite(override.eyeHeightOverride)) {
            merged.eyeHeightOverride = override.eyeHeightOverride;
        }
        if (Number.isFinite(override.detectedEyeHeight)) {
            merged.detectedEyeHeight = override.detectedEyeHeight;
        }
        if (Number.isFinite(override.scale)) {
            merged.scale = override.scale;
        }
        merged.manualLock = !!override.manualLock;
    }
    return merged;
}

export function isManuallyLocked(modelId) {
    if (!modelId) return false;
    const ov = loadOverrides()[modelId];
    return !!(ov && ov.manualLock);
}

/**
 * Compute a complete pose for a character.
 *
 * @param {string|null} modelId
 * @param {number|null} runtimeEyeHeight - just-detected value (priority 3)
 * @returns {{
 *   modelY: number,
 *   platformY: number,
 *   platformScale: number,
 *   platformZ: number,
 *   eyeHeight: number,
 *   faceAnchorY: number,
 *   effectiveAnchorY: number,
 *   globalAnchorOffset: number,
 *   faceTrim: number,
 *   name: string,
 *   source: 'manual'|'override'|'runtime'|'cache'|'config'|'default'
 * }}
 */
export function computePose(modelId, runtimeEyeHeightOrOpts = null) {
    // Back-compat: accept either a bare number (legacy) or an options
    // object: { runtimeEyeHeight, faceTrimOverride }.
    let runtimeEyeHeight = null;
    let faceTrimOverride = null;
    if (runtimeEyeHeightOrOpts && typeof runtimeEyeHeightOrOpts === "object") {
        runtimeEyeHeight = runtimeEyeHeightOrOpts.runtimeEyeHeight ?? null;
        faceTrimOverride = runtimeEyeHeightOrOpts.faceTrimOverride ?? null;
    } else if (Number.isFinite(runtimeEyeHeightOrOpts)) {
        runtimeEyeHeight = runtimeEyeHeightOrOpts;
    }

    const cfg = getPoseConfig(modelId);
    const globalAnchorOffset = getGlobalFaceAnchorOffset();
    const faceAnchorY = SCENE_FRAMING.faceAnchorY;
    const effectiveAnchorY = faceAnchorY + globalAnchorOffset;

    let eyeHeight;
    let source;
    if (cfg.manualLock && Number.isFinite(cfg.eyeHeight)) {
        eyeHeight = cfg.eyeHeight;
        source = "manual";
    } else if (Number.isFinite(cfg.eyeHeightOverride)) {
        eyeHeight = cfg.eyeHeightOverride;
        source = "override";
    } else if (Number.isFinite(runtimeEyeHeight) && runtimeEyeHeight > 0) {
        eyeHeight = runtimeEyeHeight;
        source = "runtime";
    } else if (
        Number.isFinite(cfg.detectedEyeHeight) &&
        cfg.detectedEyeHeight > 0
    ) {
        eyeHeight = cfg.detectedEyeHeight;
        source = "cache";
    } else if (
        modelId &&
        CHARACTER_POSES[modelId] &&
        Number.isFinite(CHARACTER_POSES[modelId].eyeHeight)
    ) {
        eyeHeight = CHARACTER_POSES[modelId].eyeHeight;
        source = "config";
    } else {
        eyeHeight = DEFAULT_EYE_HEIGHT;
        source = "default";
    }

    const faceTrim = Number.isFinite(faceTrimOverride)
        ? faceTrimOverride
        : cfg.faceTrim || 0;
    const platformScale = Number.isFinite(cfg.scale)
        ? cfg.scale
        : SCENE_FRAMING.platformScale;

    const modelY = effectiveAnchorY - eyeHeight + faceTrim;
    const platformY = modelY - SCENE_FRAMING.platformVerticalOffset;

    return {
        modelY,
        platformY,
        platformScale,
        platformZ: SCENE_FRAMING.platformZ,
        eyeHeight,
        faceAnchorY,
        effectiveAnchorY,
        globalAnchorOffset,
        faceTrim,
        name: cfg.name,
        source,
    };
}

const EYE_NODE_PATTERNS = [
    // High-priority: explicit left/right eye joints. Covers GLB and FBX
    // variants:
    //   GLB / Mixamo:        LeftEye, RightEye, mixamorig:LeftEye,
    //                        LeftEyeBall, RightEyeBall, LeftEyeSocket
    //   FBX / DCC variants:  EyeLeft, EyeRight, Eye_L, Eye_R, Eye.L,
    //                        eye_L, eye_R, eyeL, eyeR
    // The trailing `\b` in `eye[_.\-\s]?[lr]\b` rejects eyelash / eyelid
    // / eyebrow because the next char is a word char (i, d, b) so no
    // word boundary fires after the [lr].
    /lefteye|righteye|eyeleft|eyeright|eye[_.\-\s]?[lr]\b/i,
    /\beye\b/i,
    /head(?!less|set|phones)/i,
];

/**
 * Walk the model's skeleton/hierarchy looking for eye / head nodes and
 * return the highest-priority match's worldY minus the model's own Y.
 * Falls back to bounding-box height * ANTHROPOMETRIC_EYE_RATIO.
 *
 * Model must already be in the scene (or have updateMatrixWorld called)
 * so getWorldPosition returns a meaningful value.
 */
export function detectEyeHeight(model, opts = null) {
    if (!model) return null;
    // When skeletonOnly:true, skip the Box3-based fallback so callers
    // (the GLB auto-fit pipeline) can distinguish "real skeleton-derived
    // eye height" from "guessed-from-bounding-box" and route to a
    // dedicated body-height path instead.
    const skeletonOnly = !!(opts && opts.skeletonOnly);
    try {
        model.updateMatrixWorld(true);
    } catch (_e) {
        /* noop */
    }
    const baseY = model.position ? model.position.y : 0;

    const matches = EYE_NODE_PATTERNS.map(() => []);
    model.traverse?.((node) => {
        if (!node || !node.name) return;
        for (let i = 0; i < EYE_NODE_PATTERNS.length; i++) {
            if (EYE_NODE_PATTERNS[i].test(node.name)) {
                matches[i].push(node);
            }
        }
    });

    for (const group of matches) {
        if (group.length === 0) continue;
        let sum = 0;
        let count = 0;
        const tmp = new THREE.Vector3();
        for (const node of group) {
            try {
                node.getWorldPosition(tmp);
                if (Number.isFinite(tmp.y)) {
                    sum += tmp.y;
                    count++;
                }
            } catch (_e) {
                /* skip */
            }
        }
        if (count > 0) {
            const eyeWorldY = sum / count;
            const eyeHeight = eyeWorldY - baseY;
            if (Number.isFinite(eyeHeight) && eyeHeight > 0) {
                return eyeHeight;
            }
        }
    }

    // Fallback: bounding box height (skipped when skeletonOnly:true so
    // the GLB auto-fit pipeline can choose its own dedicated fallback).
    if (!skeletonOnly) {
        try {
            const box = new THREE.Box3().setFromObject(model);
            const size = box.getSize(new THREE.Vector3());
            if (Number.isFinite(size.y) && size.y > 0) {
                return size.y * ANTHROPOMETRIC_EYE_RATIO;
            }
        } catch (_e) {
            /* noop */
        }
    }
    return null;
}

export function listCharacterIds() {
    return Object.keys(CHARACTER_POSES);
}

/**
 * Export a merged map of base + overrides, suitable for pasting back into
 * CHARACTER_POSES as new defaults.
 */
export function exportPoses() {
    const overrides = loadOverrides();
    const merged = {};
    const ids = new Set([
        ...Object.keys(CHARACTER_POSES),
        ...Object.keys(overrides),
    ]);
    for (const id of ids) {
        const base = CHARACTER_POSES[id] || {};
        const ov = overrides[id] || {};
        merged[id] = {
            name: base.name || ov.name || "Unknown",
            eyeHeight: Number.isFinite(ov.eyeHeight)
                ? ov.eyeHeight
                : Number.isFinite(base.eyeHeight)
                  ? base.eyeHeight
                  : DEFAULT_EYE_HEIGHT,
            faceTrim: Number.isFinite(ov.faceTrim)
                ? ov.faceTrim
                : Number.isFinite(base.faceTrim)
                  ? base.faceTrim
                  : 0,
        };
        if (ov.manualLock) merged[id].manualLock = true;
        if (Number.isFinite(ov.detectedEyeHeight)) {
            merged[id].detectedEyeHeight = ov.detectedEyeHeight;
        }
    }
    return merged;
}
